import React, { useMemo, useState } from 'react';
import { collection, doc, getDocs, limit, query, updateDoc, where, arrayUnion } from 'firebase/firestore';
import { db } from '../../firebase';
import { adminInviteActionSettings } from '../../components/selfServeSignup';
import { sendFleetInvite } from '../../components/FleetJoinPage';
import { isPaymentsConfigured, sendAdminInviteEmailApi } from '../../services/paymentsApi';
import { useAuth } from '../../contexts/AuthContext';
import { PieChart, Pie, Cell } from 'recharts';
import FleetIcon from './FleetIcon';
import { FleetConsoleMap, FleetConsoleVehicleMap } from './FleetConsoleMap';
import {
  estimateFuelEfficiency,
  ratingColorFromLabel,
  ratingFromPct,
  vehicleBenchmarkKmPerL,
  vehicleTankL,
} from '../fleetFuelEfficiency';
import { downloadCsv } from '../../utils/downloadCsv';
import type { FleetAlertRow } from '../useFleetAlerts';
import {
  refuelEventCostKsh,
  refuelEventLiters,
  refuelEventTimestampMs,
  refuelEventVehicleId,
} from '../fleetRefuelUtils';

export type VehicleRow = {
  id: string;
  plate: string;
  type: string;
  driver: string;
  status: 'trip' | 'idle' | 'maint';
  km30: number;
  eff: number | null;
  rating: string;
  cost: number;
  color: string;
  routePoints?: { lat: number; lng: number; gapBefore?: boolean; estimated?: boolean }[];
};

const STATUS_META = {
  trip: { d: '#1E7A4C', l: 'On trip', bg: '#E4F1E9', fg: '#1E7A4C' },
  idle: { d: '#8C9196', l: 'Idle', bg: '#EFEDE7', fg: '#5D646B' },
  maint: { d: '#9A5B00', l: 'Maintenance', bg: '#FBEFD9', fg: '#9A5B00' },
};

function ratingColor(r: string): string {
  if (r === 'Excellent') return '#1E7A4C';
  if (r === 'Good') return '#00707F';
  if (r === 'Fair') return '#9A5B00';
  return '#B42318';
}

function tsMs(v: unknown): number {
  if (v && typeof v === 'object' && 'toDate' in (v as object)) {
    return ((v as { toDate: () => Date }).toDate?.() ?? new Date()).getTime();
  }
  if (typeof v === 'number') return v;
  return 0;
}

function formatDate(v: unknown): string {
  const ms = tsMs(v);
  if (!ms) return '—';
  return new Date(ms).toLocaleString(undefined, { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' });
}

function fleetPlateKey(id: string): string {
  return String(id || '')
    .toLowerCase()
    .replace(/[\s-]/g, '');
}

/** Whether a trip/refuel busId matches a fleet vehicle row (plate vs org-scoped doc id). */
export function tripBelongsToVehicle(vehicle: VehicleRow, row: Record<string, unknown>): boolean {
  const busId = String(row.busId ?? row.lorryId ?? '');
  if (!busId) return false;
  const key = fleetPlateKey(busId);
  return (
    vehicle.id === busId ||
    fleetPlateKey(vehicle.id) === key ||
    fleetPlateKey(vehicle.plate) === key ||
    vehicle.id.toLowerCase().endsWith(`__${key}`)
  );
}

export function isCompletedFleetTrip(t: Record<string, unknown>): boolean {
  if (t.routeFinishedAt != null) return true;
  const status = String(t.status ?? '').toLowerCase();
  return status === 'completed' || status === 'finished';
}

/** Match trip/issue busId (plate) to fleet vehicle doc id. */
export function resolveVehiclePlate(
  vehicles: VehicleRow[],
  busId: string,
  formatPlate: (id: string) => string
): string {
  if (!busId) return '—';
  const key = fleetPlateKey(busId);
  const hit = vehicles.find(
    (v) =>
      v.id === busId ||
      fleetPlateKey(v.id) === key ||
      fleetPlateKey(v.plate) === key ||
      v.id.toLowerCase().endsWith(`__${key}`)
  );
  return hit?.plate ?? formatPlate(busId);
}

function formatTripDuration(start: unknown, end: unknown, status?: unknown): string {
  const s = tsMs(start);
  if (!s) return '—';
  const endMs = tsMs(end) || (status === 'active' || status === 'collecting' ? Date.now() : 0);
  if (!endMs) return '—';
  const secs = Math.max(0, Math.floor((endMs - s) / 1000));
  if (secs < 3600) {
    const m = Math.floor(secs / 60);
    const sec = secs % 60;
    return `${m}m ${String(sec).padStart(2, '0')}s`;
  }
  const h = Math.floor(secs / 3600);
  const m = Math.floor((secs % 3600) / 60);
  return `${h}h ${String(m).padStart(2, '0')}m`;
}

type KpiProps = {
  totalKm30: number;
  liveCount: number;
  vehicleCount: number;
  avgEff: number;
  fuelSpend30: number;
  needsRefuelCount: number;
  refuelLitres30: number;
  refuelCount30: number;
  onRefuelAuditClick?: () => void;
};

export function FleetKpiRow(p: KpiProps) {
  const cards = [
    { l: 'Distance · 30d', v: p.totalKm30.toLocaleString(undefined, { maximumFractionDigits: 0 }), u: 'km', ic: 'route', d: p.liveCount > 0 ? 'live' : '—', up: true, click: undefined as (() => void) | undefined },
    { l: 'Needs refuel', v: String(p.needsRefuelCount), u: 'trips flagged', ic: 'fuel', d: '30d', up: p.needsRefuelCount === 0, click: undefined },
    { l: 'Fleet avg efficiency', v: p.avgEff ? p.avgEff.toFixed(1) : '—', u: 'km/L', ic: 'gauge', d: '30d', up: p.avgEff >= 3.5, click: undefined },
    { l: 'Fuel spend · 30d', v: p.fuelSpend30 >= 1000 ? `KSh ${Math.round(p.fuelSpend30 / 1000)}k` : `KSh ${Math.round(p.fuelSpend30)}`, u: '', ic: 'fuel', d: `${p.refuelCount30} refuels`, up: false, click: p.onRefuelAuditClick },
  ];
  return (
    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 14 }}>
      {cards.map((k, i) => (
        <div
          key={i}
          role={k.click ? 'button' : undefined}
          tabIndex={k.click ? 0 : undefined}
          onClick={k.click}
          onKeyDown={k.click ? (e) => e.key === 'Enter' && k.click?.() : undefined}
          style={{
            background: '#fff',
            border: '1px solid #E4E3DE',
            borderRadius: 18,
            padding: '16px',
            cursor: k.click ? 'pointer' : 'default',
          }}
        >
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <div style={{ width: 38, height: 38, borderRadius: 11, background: '#E2F5F8', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <FleetIcon name={k.ic} size={18} color="#00707F" />
            </div>
            <div style={{ fontSize: 11, fontWeight: 700, color: k.up ? '#1E7A4C' : '#5D646B', background: (k.up ? '#1E7A4C' : '#5D646B') + '14', padding: '3px 8px', borderRadius: 7 }}>{k.d}</div>
          </div>
          <div style={{ display: 'flex', alignItems: 'baseline', gap: 5, marginTop: 14 }}>
            <div style={{ fontSize: 26, fontWeight: 700, fontFamily: 'JetBrains Mono, monospace', letterSpacing: -0.5, whiteSpace: 'nowrap' }}>{k.v}</div>
            <div style={{ fontSize: 12, color: '#8C9196' }}>{k.u}</div>
          </div>
          <div style={{ fontSize: 11.5, color: '#5D646B', marginTop: 3, letterSpacing: 0.3, textTransform: 'uppercase' }}>{k.l}</div>
        </div>
      ))}
    </div>
  );
}

export function EfficiencyDonut({ pct, label }: { pct: number | null; label: string }) {
  const score = pct ?? 0;
  const data = [
    { name: 'score', value: Math.max(0, Math.min(100, score)) },
    { name: 'rest', value: Math.max(0, 100 - Math.max(0, Math.min(100, score))) },
  ];
  const col = ratingColorFromLabel(label);
  return (
    <div style={{ width: 120, height: 120, position: 'relative' }}>
      <PieChart width={120} height={120}>
        <Pie data={data} dataKey="value" cx="50%" cy="50%" innerRadius={38} outerRadius={52} startAngle={90} endAngle={-270}>
          <Cell fill={col} />
          <Cell fill="#EFEDE7" />
        </Pie>
      </PieChart>
      <div style={{ position: 'absolute', inset: 0, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center' }}>
        <div style={{ fontSize: 18, fontWeight: 700, fontFamily: 'JetBrains Mono, monospace' }}>{pct != null ? `${Math.round(pct)}%` : '—'}</div>
        <div style={{ fontSize: 9, color: '#5D646B', fontWeight: 600 }}>{label}</div>
      </div>
    </div>
  );
}

type VehicleTableProps = {
  vehicles: VehicleRow[];
  driverNames: Record<string, string>;
  onRegister: () => void;
  onSelect: (id: string) => void;
  showRegister?: boolean;
};

export function VehicleTable({ vehicles, driverNames, onRegister, onSelect, showRegister = true }: VehicleTableProps) {
  return (
    <div style={{ background: '#fff', border: '1px solid #E4E3DE', borderRadius: 18, overflow: 'hidden' }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '14px 16px' }}>
        <div style={{ fontSize: 14, fontWeight: 700 }}>Vehicles</div>
        {showRegister && (
          <button type="button" onClick={onRegister} style={{ display: 'flex', alignItems: 'center', gap: 7, padding: '8px 13px', background: '#00707F', color: '#fff', border: 'none', borderRadius: 10, fontSize: 12.5, fontWeight: 600, fontFamily: 'inherit', cursor: 'pointer' }}>
            <FleetIcon name="plus" size={14} color="#fff" /> Register vehicle
          </button>
        )}
      </div>
      <div style={{ display: 'grid', gridTemplateColumns: '1.4fr 1.2fr 0.9fr 0.9fr 1.2fr 1fr 60px', padding: '0 16px 8px', fontSize: 10.5, fontWeight: 700, color: '#8C9196', letterSpacing: 0.5, textTransform: 'uppercase', borderBottom: '1px solid #EFEDE7' }}>
        <div>Vehicle</div><div>Driver</div><div>Status</div><div style={{ textAlign: 'right' }}>Dist · 30d</div><div style={{ textAlign: 'right' }}>Efficiency</div><div style={{ textAlign: 'right' }}>Fuel cost</div><div />
      </div>
      {vehicles.map((v, i) => {
        const sm = STATUS_META[v.status];
        const driverLabel = v.driver !== 'Unassigned' ? v.driver : Object.values(driverNames)[0] || 'Unassigned';
        return (
          <div key={v.id} role="button" tabIndex={0} onClick={() => onSelect(v.id)} onKeyDown={(e) => e.key === 'Enter' && onSelect(v.id)} style={{ display: 'grid', gridTemplateColumns: '1.4fr 1.2fr 0.9fr 0.9fr 1.2fr 1fr 60px', alignItems: 'center', padding: '13px 16px', borderBottom: i < vehicles.length - 1 ? '1px solid #EFEDE7' : 'none', cursor: 'pointer' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
              <div style={{ width: 34, height: 34, borderRadius: 9, background: '#F6F5F1', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <FleetIcon name="truck" size={16} color="#00707F" />
              </div>
              <div>
                <div style={{ fontSize: 13, fontWeight: 700, fontFamily: 'JetBrains Mono, monospace', letterSpacing: 0.4 }}>{v.plate}</div>
                <div style={{ fontSize: 11, color: '#5D646B' }}>{v.type}</div>
              </div>
            </div>
            <div style={{ fontSize: 12.5, color: driverLabel === 'Unassigned' ? '#8C9196' : '#0F1419', fontStyle: driverLabel === 'Unassigned' ? 'italic' : 'normal' }}>{driverLabel}</div>
            <div>
              <span style={{ display: 'inline-flex', alignItems: 'center', gap: 5, padding: '4px 9px', background: sm.bg, borderRadius: 20 }}>
                <span style={{ width: 6, height: 6, borderRadius: 3, background: sm.d }} />
                <span style={{ fontSize: 10.5, fontWeight: 700, color: sm.fg }}>{sm.l}</span>
              </span>
            </div>
            <div style={{ textAlign: 'right', fontSize: 13, fontWeight: 600, fontFamily: 'JetBrains Mono, monospace' }}>{v.km30.toLocaleString()}</div>
            <div style={{ textAlign: 'right' }}>
              {v.eff != null ? (
                <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
                  <span style={{ fontSize: 13, fontWeight: 600, fontFamily: 'JetBrains Mono, monospace' }}>{v.eff.toFixed(1)}</span>
                  <span style={{ fontSize: 9.5, fontWeight: 700, color: ratingColor(v.rating), padding: '2px 6px', background: ratingColor(v.rating) + '1A', borderRadius: 5 }}>{v.rating}</span>
                </span>
              ) : (
                <span style={{ color: '#8C9196', fontSize: 12 }}>—</span>
              )}
            </div>
            <div style={{ textAlign: 'right', fontSize: 12.5, fontFamily: 'JetBrains Mono, monospace', color: '#2A3138' }}>KSh {v.cost.toLocaleString()}</div>
            <div style={{ textAlign: 'right' }}><FleetIcon name="chev" size={15} color="#C2CEDD" /></div>
          </div>
        );
      })}
      {vehicles.length === 0 && <div style={{ padding: 32, textAlign: 'center', color: '#5D646B', fontSize: 14 }}>No vehicles registered for this fleet yet.</div>}
    </div>
  );
}

export function LiveMapSection({
  vehicles,
  vehicleDocs,
  restByVehicleId,
  liveCount,
  orgSubtitle,
}: {
  vehicles: VehicleRow[];
  vehicleDocs: Record<string, Record<string, unknown>>;
  restByVehicleId: Record<string, { lat: number; lng: number }>;
  liveCount: number;
  orgSubtitle: string;
}) {
  const mapVehicles: FleetConsoleVehicleMap[] = useMemo(() => {
    return vehicles.map((v) => {
      const raw = vehicleDocs[v.id] || {};
      const live = raw.liveLocation as Record<string, unknown> | undefined;
      const lat = Number(live?.lat);
      const lng = Number(live?.lng);
      const routePoints =
        (raw.routePoints as { lat?: number; lng?: number; gapBefore?: boolean; estimated?: boolean }[] | undefined)
          ?.map((p) => ({ lat: Number(p.lat), lng: Number(p.lng), gapBefore: p.gapBefore === true, estimated: p.estimated === true }))
          .filter((p) => !Number.isNaN(p.lat) && !Number.isNaN(p.lng)) ?? v.routePoints ?? [];
      return {
        id: v.id,
        plate: v.plate,
        name: v.type,
        status: v.status,
        liveLocation:
          !Number.isNaN(lat) && !Number.isNaN(lng) ? { lat, lng } : null,
        routePoints,
        restLocation: restByVehicleId[v.id] ?? null,
      };
    });
  }, [vehicles, vehicleDocs, restByVehicleId]);

  return (
    <div style={{ display: 'grid', gridTemplateColumns: '1fr', gap: 14 }}>
      <div style={{ background: '#fff', border: '1px solid #E4E3DE', borderRadius: 18, overflow: 'hidden' }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '14px 16px' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 9 }}>
            <div style={{ fontSize: 14, fontWeight: 700 }}>Live fleet map</div>
            {liveCount > 0 && (
              <div style={{ display: 'flex', alignItems: 'center', gap: 5, padding: '3px 8px', background: '#E4F1E9', borderRadius: 20 }}>
                <div style={{ width: 6, height: 6, borderRadius: 3, background: '#1E7A4C' }} />
                <span style={{ fontSize: 10.5, fontWeight: 700, color: '#1E7A4C' }}>{liveCount} LIVE</span>
              </div>
            )}
          </div>
        </div>
        <div style={{ padding: '0 12px 12px' }}>
          <FleetConsoleMap vehicles={mapVehicles} orgSubtitle={orgSubtitle} />
        </div>
      </div>
    </div>
  );
}

export function AlertsSidebar({
  alerts,
  onDismiss,
  onDismissAll,
}: {
  alerts: FleetAlertRow[];
  onDismiss: (id: string) => void;
  onDismissAll: () => void;
}) {
  const unread = alerts.filter((a) => !a.read);
  return (
    <div style={{ background: '#fff', border: '1px solid #E4E3DE', borderRadius: 18, padding: '14px 16px', display: 'flex', flexDirection: 'column' }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <div style={{ fontSize: 14, fontWeight: 700 }}>Alerts</div>
        {unread.length > 0 && (
          <button type="button" onClick={onDismissAll} style={{ fontSize: 11, fontWeight: 600, color: '#00707F', background: 'none', border: 'none', cursor: 'pointer', fontFamily: 'inherit' }}>
            Clear all
          </button>
        )}
      </div>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 9, marginTop: 12, maxHeight: 420, overflow: 'auto' }}>
        {(alerts.length ? alerts : []).map((a) => (
          <div key={a.id} style={{ display: 'flex', gap: 10, padding: '10px', background: a.read ? '#FAF9F6' : '#FFF8E8', border: '1px solid #EFEDE7', borderRadius: 13 }}>
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{ fontSize: 12.5, fontWeight: 600, lineHeight: 1.25 }}>{a.message}</div>
              <div style={{ fontSize: 11, color: '#5D646B', marginTop: 2 }}>{a.timestamp.toLocaleString()}</div>
            </div>
            {!a.read && (
              <button type="button" onClick={() => onDismiss(a.id)} style={{ fontSize: 10, fontWeight: 600, color: '#00707F', background: 'none', border: 'none', cursor: 'pointer', fontFamily: 'inherit' }}>
                Mark read
              </button>
            )}
          </div>
        ))}
        {alerts.length === 0 && <div style={{ fontSize: 12, color: '#5D646B' }}>All clear — no alerts.</div>}
      </div>
    </div>
  );
}

export function TripsAuditTable({
  trips,
  vehicles,
  formatPlate,
  onExportCsv,
  title = 'Trip audit',
  driverNames,
  maxRows = 50,
}: {
  trips: Record<string, unknown>[];
  vehicles: VehicleRow[];
  formatPlate: (id: string) => string;
  onExportCsv: () => void;
  title?: string;
  driverNames?: Record<string, string>;
  maxRows?: number;
}) {
  const sorted = [...trips].sort((a, b) => tsMs(b.routeFinishedAt ?? b.routeStartedAt) - tsMs(a.routeFinishedAt ?? a.routeStartedAt));
  return (
    <div style={{ background: '#fff', border: '1px solid #E4E3DE', borderRadius: 18, overflow: 'hidden' }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '14px 16px' }}>
        <div style={{ fontSize: 14, fontWeight: 700 }}>{title}</div>
        <button type="button" onClick={onExportCsv} style={{ padding: '8px 12px', background: '#F6F5F1', border: '1px solid #E4E3DE', borderRadius: 10, fontSize: 12, fontWeight: 600, cursor: 'pointer', fontFamily: 'inherit' }}>
          Export CSV
        </button>
      </div>
      <div style={{ overflowX: 'auto' }}>
        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12 }}>
          <thead>
            <tr style={{ textAlign: 'left', color: '#8C9196', fontSize: 10.5, textTransform: 'uppercase' }}>
              <th style={{ padding: '8px 16px' }}>Date</th>
              <th style={{ padding: '8px 16px' }}>Vehicle</th>
              <th style={{ padding: '8px 16px' }}>Duration</th>
              <th style={{ padding: '8px 16px' }}>Km</th>
              <th style={{ padding: '8px 16px' }}>Driver</th>
              <th style={{ padding: '8px 16px' }}>Refuel</th>
              <th style={{ padding: '8px 16px' }}>Status</th>
            </tr>
          </thead>
          <tbody>
            {sorted.length === 0 && (
              <tr>
                <td colSpan={7} style={{ padding: '20px 16px', color: '#5D646B' }}>
                  No trips recorded yet. Completed trips appear here after drivers end routes.
                </td>
              </tr>
            )}
            {sorted.slice(0, maxRows).map((t) => {
              const busId = String(t.busId ?? t.lorryId ?? '');
              const driverId = String(t.driverId ?? '');
              const status = String(t.status ?? (t.routeFinishedAt ? 'completed' : 'active'));
              return (
                <tr key={String(t.id)} style={{ borderTop: '1px solid #EFEDE7' }}>
                  <td style={{ padding: '10px 16px' }}>{formatDate(t.routeFinishedAt ?? t.routeStartedAt)}</td>
                  <td style={{ padding: '10px 16px', fontFamily: 'JetBrains Mono, monospace' }}>
                    {resolveVehiclePlate(vehicles, busId, formatPlate)}
                  </td>
                  <td style={{ padding: '10px 16px' }}>
                    {formatTripDuration(t.routeStartedAt, t.routeFinishedAt, status)}
                  </td>
                  <td style={{ padding: '10px 16px' }}>{Number(t.distanceKm ?? 0).toFixed(1)}</td>
                  <td style={{ padding: '10px 16px' }}>{driverNames?.[driverId] || (driverId ? `${driverId.slice(0, 8)}…` : '—')}</td>
                  <td style={{ padding: '10px 16px' }}>{t.needsRefuelSoon ? 'Flagged' : '—'}</td>
                  <td style={{ padding: '10px 16px', textTransform: 'capitalize' }}>{status}</td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}

/** Overview: trip history + recent refuel events. */
export function OverviewFleetHistory({
  trips,
  refuelEvents,
  vehicles,
  driverNames,
  formatPlate,
  onExportTripsCsv,
}: {
  trips: Record<string, unknown>[];
  refuelEvents: Record<string, unknown>[];
  vehicles: VehicleRow[];
  driverNames: Record<string, string>;
  formatPlate: (id: string) => string;
  onExportTripsCsv: () => void;
}) {
  const thirtyAgo = Date.now() - 30 * 24 * 60 * 60 * 1000;
  const refuelRecent = [...refuelEvents]
    .filter((e) => refuelEventTimestampMs(e) >= thirtyAgo)
    .sort((a, b) => refuelEventTimestampMs(b) - refuelEventTimestampMs(a))
    .slice(0, 12);

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
      <TripsAuditTable
        title="Trip history"
        trips={trips}
        vehicles={vehicles}
        formatPlate={formatPlate}
        driverNames={driverNames}
        onExportCsv={onExportTripsCsv}
        maxRows={40}
      />
      <div style={{ background: '#fff', border: '1px solid #E4E3DE', borderRadius: 18, padding: 16 }}>
        <div style={{ fontSize: 14, fontWeight: 700, marginBottom: 10 }}>Refuel events · 30 days</div>
        {refuelRecent.length === 0 && (
          <div style={{ color: '#5D646B', fontSize: 12.5 }}>No refuel logs in the last 30 days.</div>
        )}
        {refuelRecent.map((e) => (
          <div
            key={String(e.id)}
            style={{
              display: 'flex',
              justifyContent: 'space-between',
              padding: '10px 0',
              borderBottom: '1px solid #EFEDE7',
              fontSize: 12.5,
            }}
          >
            <span>
              {formatDate(e.timestamp ?? e.recordedAt ?? e.createdAt)} ·{' '}
              {resolveVehiclePlate(vehicles, refuelEventVehicleId(e), formatPlate)}
            </span>
            <span style={{ fontFamily: 'JetBrains Mono, monospace' }}>
              {refuelEventLiters(e).toFixed(1)} L · KSh {refuelEventCostKsh(e).toLocaleString()}
            </span>
          </div>
        ))}
      </div>
    </div>
  );
}

export function exportTripsCsv(
  trips: Record<string, unknown>[],
  formatPlate: (id: string) => string,
  vehicles: VehicleRow[] = []
) {
  const rows: string[][] = [['Date', 'Vehicle', 'Duration', 'Km', 'Status', 'Needs refuel']];
  trips.forEach((t) => {
    const busId = String(t.busId ?? t.lorryId ?? '');
    const status = String(t.status ?? '');
    rows.push([
      formatDate(t.routeFinishedAt ?? t.routeStartedAt),
      vehicles.length ? resolveVehiclePlate(vehicles, busId, formatPlate) : formatPlate(busId),
      formatTripDuration(t.routeStartedAt, t.routeFinishedAt, status),
      String(Number(t.distanceKm ?? 0).toFixed(1)),
      status,
      t.needsRefuelSoon ? 'yes' : 'no',
    ]);
  });
  downloadCsv('fleet-trips.csv', rows);
}

export function FuelLogsView({ events }: { events: Record<string, unknown>[] }) {
  const thirtyAgo = Date.now() - 30 * 24 * 60 * 60 * 1000;
  const recent = events.filter((e) => refuelEventTimestampMs(e) >= thirtyAgo);
  const litres = recent.reduce((s, e) => s + refuelEventLiters(e), 0);
  const cost = recent.reduce((s, e) => s + refuelEventCostKsh(e), 0);
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
      <FleetKpiRow totalKm30={0} liveCount={0} vehicleCount={0} avgEff={0} fuelSpend30={cost} needsRefuelCount={0} refuelLitres30={litres} refuelCount30={recent.length} />
      <div style={{ background: '#fff', border: '1px solid #E4E3DE', borderRadius: 18, padding: 16 }}>
        <div style={{ fontSize: 14, fontWeight: 700, marginBottom: 12 }}>Refuel events · 30 days</div>
        {recent.slice(0, 40).map((e) => (
          <div key={String(e.id)} style={{ display: 'flex', justifyContent: 'space-between', padding: '10px 0', borderBottom: '1px solid #EFEDE7', fontSize: 12.5 }}>
            <span>{formatDate(e.timestamp ?? e.recordedAt ?? e.createdAt)} · {refuelEventVehicleId(e)}</span>
            <span style={{ fontFamily: 'JetBrains Mono, monospace' }}>
              {refuelEventLiters(e).toFixed(1)} L · KSh {refuelEventCostKsh(e).toLocaleString()}
              {e.efficiencyFlag && e.efficiencyFlag !== 'ok' ? ` · ${String(e.efficiencyFlag)}` : ''}
            </span>
          </div>
        ))}
        {recent.length === 0 && <div style={{ color: '#5D646B' }}>No refuel events in the last 30 days.</div>}
      </div>
    </div>
  );
}

export function IncidentsView({
  issues,
  vehicles,
  formatPlate,
  onExportCsv,
}: {
  issues: Record<string, unknown>[];
  vehicles: VehicleRow[];
  formatPlate: (id: string) => string;
  onExportCsv: () => void;
}) {
  const byVehicleMonth: Record<string, Record<string, number>> = {};
  issues.forEach((iss) => {
    const vid = String(iss.busId ?? iss.lorryId ?? '');
    const ms = tsMs(iss.timestamp ?? iss.tripFinishedAt ?? iss.createdAt ?? iss.reportedAt);
    if (!ms) return;
    const key = new Date(ms).toLocaleString(undefined, { year: 'numeric', month: 'short' });
    if (!byVehicleMonth[vid]) byVehicleMonth[vid] = {};
    byVehicleMonth[vid][key] = (byVehicleMonth[vid][key] || 0) + 1;
  });
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
      <div style={{ background: '#fff', border: '1px solid #E4E3DE', borderRadius: 18, overflow: 'hidden' }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '14px 16px' }}>
          <div style={{ fontSize: 14, fontWeight: 700 }}>Incidents</div>
          <button type="button" onClick={onExportCsv} style={{ padding: '8px 12px', background: '#F6F5F1', border: '1px solid #E4E3DE', borderRadius: 10, fontSize: 12, fontWeight: 600, cursor: 'pointer', fontFamily: 'inherit' }}>
            Monthly CSV
          </button>
        </div>
        {issues.length === 0 && (
          <div style={{ padding: '20px 16px', color: '#5D646B', fontSize: 12.5 }}>
            No incident reports yet. Drivers can file an issue from the post-trip survey after ending a route.
          </div>
        )}
        {issues.slice(0, 40).map((iss) => {
          const busId = String(iss.busId ?? iss.lorryId ?? '');
          const routeId = String(iss.routeId ?? '');
          const source = String(iss.source ?? '');
          return (
            <div key={String(iss.id)} style={{ padding: '12px 16px', borderTop: '1px solid #EFEDE7' }}>
              <div style={{ fontSize: 13, fontWeight: 600 }}>
                {String(iss.issueType ?? iss.severity ?? 'Incident')} ·{' '}
                {resolveVehiclePlate(vehicles, busId, formatPlate)}
              </div>
              <div style={{ fontSize: 12, color: '#2A3138', marginTop: 4 }}>
                {String(iss.details ?? iss.description ?? iss.message ?? '').trim() || 'No details provided.'}
              </div>
              <div style={{ fontSize: 11, color: '#8C9196', marginTop: 4 }}>
                {formatDate(iss.timestamp ?? iss.tripFinishedAt ?? iss.createdAt ?? iss.reportedAt)}
                {routeId ? ` · Trip ${routeId.slice(0, 8)}…` : ''}
                {source === 'post_trip_survey' ? ' · Post-trip survey' : ''}
              </div>
            </div>
          );
        })}
      </div>
      <div style={{ background: '#fff', border: '1px solid #E4E3DE', borderRadius: 18, padding: 16 }}>
        <div style={{ fontSize: 14, fontWeight: 700, marginBottom: 10 }}>Monthly count per vehicle</div>
        {vehicles.map((v) => {
          const months = byVehicleMonth[v.id] || {};
          const entries = Object.entries(months);
          if (!entries.length) return null;
          return (
            <div key={v.id} style={{ marginBottom: 8, fontSize: 12.5 }}>
              <span style={{ fontWeight: 600, fontFamily: 'JetBrains Mono, monospace' }}>{v.plate}</span>
              {entries.map(([m, c]) => (
                <span key={m} style={{ marginLeft: 10, color: '#5D646B' }}>{m}: {c}</span>
              ))}
            </div>
          );
        })}
      </div>
    </div>
  );
}

export function exportIncidentsMonthlyCsv(issues: Record<string, unknown>[], formatPlate: (id: string) => string) {
  const rows: string[][] = [['Vehicle', 'Month', 'Count']];
  const agg: Record<string, number> = {};
  issues.forEach((iss) => {
    const vid = String(iss.busId ?? iss.lorryId ?? '');
    const ms = tsMs(iss.timestamp ?? iss.tripFinishedAt ?? iss.createdAt ?? iss.reportedAt);
    const key = `${formatPlate(vid)}|${new Date(ms).toLocaleString(undefined, { year: 'numeric', month: 'short' })}`;
    agg[key] = (agg[key] || 0) + 1;
  });
  Object.entries(agg).forEach(([k, c]) => {
    const [vehicle, month] = k.split('|');
    rows.push([vehicle, month, String(c)]);
  });
  downloadCsv('fleet-incidents-monthly.csv', rows);
}

export function VehicleDetailScreen({
  vehicle,
  raw,
  isCargo,
  tripHistory,
  refuelEvents,
  issueReports,
  driverRows,
  restLocation,
  onBack,
  onSave,
  onToggleMaint,
}: {
  vehicle: VehicleRow;
  raw: Record<string, unknown>;
  isCargo: boolean;
  tripHistory: Record<string, unknown>[];
  refuelEvents: Record<string, unknown>[];
  issueReports: Record<string, unknown>[];
  driverRows: Array<{ uid: string; name: string; vehicleId?: string; isPrimary: boolean }>;
  restLocation?: { lat: number; lng: number } | null;
  onBack: () => void;
  onSave: (tank: string, bench: string) => Promise<void>;
  onToggleMaint: () => Promise<void>;
}) {
  const thirtyAgo = Date.now() - 30 * 24 * 60 * 60 * 1000;
  const [tankInput, setTankInput] = useState(() => String(vehicleTankL(raw)));
  const [benchInput, setBenchInput] = useState(() => {
    const bench = vehicleBenchmarkKmPerL(raw);
    return bench != null ? String(bench) : '';
  });
  const [saving, setSaving] = useState(false);

  const vehicleTrips = useMemo(
    () => tripHistory.filter((t) => tripBelongsToVehicle(vehicle, t)),
    [tripHistory, vehicle]
  );
  const completedTrips = useMemo(
    () =>
      vehicleTrips
        .filter((t) => isCompletedFleetTrip(t))
        .filter((t) => tsMs(t.routeFinishedAt ?? t.routeStartedAt) >= thirtyAgo),
    [vehicleTrips, thirtyAgo]
  );
  const km30 = completedTrips.reduce((s, t) => s + Number(t.distanceKm ?? 0), 0);
  const tripCount30 = completedTrips.length;

  const vehicleRefuels = useMemo(
    () =>
      refuelEvents.filter(
        (e) =>
          tripBelongsToVehicle(vehicle, { busId: refuelEventVehicleId(e) }) &&
          refuelEventTimestampMs(e) >= thirtyAgo
      ),
    [refuelEvents, vehicle, thirtyAgo]
  );
  const refuelCount30 = vehicleRefuels.length;
  const totalLitres = vehicleRefuels.reduce((s, e) => s + refuelEventLiters(e), 0);
  const kmPerL = totalLitres > 0 && km30 > 0 ? km30 / totalLitres : null;

  const bench = vehicleBenchmarkKmPerL(raw);
  const eff = estimateFuelEfficiency({
    totalKm30: km30,
    refuelCount30,
    needsRefuelCount: 0,
    totalLitresAdded: totalLitres > 0 ? totalLitres : undefined,
    tankCapacityL: vehicleTankL(raw),
    benchmarkKmPerL: bench,
  });
  const rating = ratingFromPct(eff.pct);

  const normalizePlateId = (id: string) => id.replace(/[\s-]/g, '').toUpperCase();
  const vehicleDrivers = driverRows.filter(
    (d) => d.vehicleId && normalizePlateId(d.vehicleId) === normalizePlateId(vehicle.id)
  );
  const vehicleIssues = issueReports.filter((i) => tripBelongsToVehicle(vehicle, i));

  const damageByTripId: Record<string, string[]> = {};
  vehicleIssues.forEach((iss) => {
    const tid = String(iss.routeId ?? iss.tripId ?? '');
    if (!damageByTripId[tid]) damageByTripId[tid] = [];
    damageByTripId[tid].push(String(iss.description ?? iss.message ?? ''));
  });

  const lastTrip = useMemo(() => {
    return [...vehicleTrips]
      .filter((t) => isCompletedFleetTrip(t))
      .sort((a, b) => tsMs(b.routeFinishedAt ?? b.routeStartedAt) - tsMs(a.routeFinishedAt ?? a.routeStartedAt))[0];
  }, [vehicleTrips]);

  const lastTripRoutePoints = useMemo(() => {
    const pts = (lastTrip?.routePoints as { lat?: number; lng?: number; gapBefore?: boolean; estimated?: boolean }[] | undefined) ?? [];
    return pts
      .map((p) => ({ lat: Number(p.lat), lng: Number(p.lng), gapBefore: p.gapBefore === true, estimated: p.estimated === true }))
      .filter((p) => !Number.isNaN(p.lat) && !Number.isNaN(p.lng));
  }, [lastTrip]);

  const sm = STATUS_META[vehicle.status];
  const cardStyle: React.CSSProperties = {
    background: '#fff',
    border: '1px solid #E4E3DE',
    borderRadius: 18,
    padding: 16,
  };
  const cardTitleStyle: React.CSSProperties = {
    fontSize: 14,
    fontWeight: 700,
    marginBottom: 12,
  };
  const inputLabelStyle: React.CSSProperties = {
    fontSize: 11,
    color: '#5D646B',
    fontWeight: 600,
    display: 'block',
  };
  const inputStyle: React.CSSProperties = {
    display: 'block',
    width: '100%',
    marginTop: 4,
    padding: '8px 10px',
    borderRadius: 8,
    border: '1px solid #E4E3DE',
    fontFamily: 'inherit',
    fontSize: 13,
  };

  const severityBadgeStyle = (sev: string): React.CSSProperties => {
    const s = sev.toLowerCase();
    const bg = s === 'major' ? '#FDECEA' : s === 'minor' ? '#FBEFD9' : '#EFEDE7';
    const fg = s === 'major' ? '#B42318' : s === 'minor' ? '#9A5B00' : '#5D646B';
    return {
      fontSize: 9,
      fontWeight: 700,
      padding: '2px 6px',
      borderRadius: 4,
      background: bg,
      color: fg,
    };
  };

  const kpiTiles = [
    { v: `${km30.toFixed(1)} km`, l: 'Distance · 30d', ic: 'route' },
    { v: String(tripCount30), l: 'Trips completed', ic: 'route' },
    { v: String(refuelCount30), l: 'Refuels · 30d', ic: 'fuel' },
    { v: kmPerL != null ? `${kmPerL.toFixed(1)} km/L` : '—', l: 'Avg efficiency', ic: 'gauge' },
  ];

  const handleSave = async () => {
    setSaving(true);
    try {
      await onSave(tankInput, benchInput);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div>
      <div style={{ display: 'flex', alignItems: 'center', gap: 14, marginBottom: 24, flexWrap: 'wrap' }}>
        <button
          type="button"
          onClick={onBack}
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: 6,
            padding: '8px 14px',
            background: '#F6F5F1',
            border: '1px solid #E4E3DE',
            borderRadius: 10,
            fontSize: 13,
            fontWeight: 600,
            cursor: 'pointer',
            fontFamily: 'inherit',
            color: '#0F1419',
          }}
        >
          ← Back
        </button>
        <div style={{ width: 44, height: 44, borderRadius: 13, background: '#F6F5F1', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <FleetIcon name="truck" size={20} color="#00707F" />
        </div>
        <div style={{ flex: 1, minWidth: 160 }}>
          <div style={{ fontSize: 20, fontWeight: 700, fontFamily: 'JetBrains Mono, monospace', letterSpacing: 0.5 }}>
            {vehicle.plate}
          </div>
          <div style={{ fontSize: 12, color: '#5D646B', marginTop: 2 }}>
            {vehicle.type} · {isCargo ? 'Cargo fleet' : 'Personal track'}
          </div>
        </div>
        <span style={{ display: 'inline-flex', alignItems: 'center', gap: 5, padding: '4px 10px', background: sm.bg, borderRadius: 20 }}>
          <span style={{ width: 6, height: 6, borderRadius: 3, background: sm.d }} />
          <span style={{ fontSize: 10.5, fontWeight: 700, color: sm.fg }}>{sm.l}</span>
        </span>
        <button
          type="button"
          onClick={() => void onToggleMaint()}
          style={{
            padding: '8px 14px',
            background: vehicle.status === 'maint' ? '#E4F1E9' : '#FBEFD9',
            color: vehicle.status === 'maint' ? '#1E7A4C' : '#9A5B00',
            border: 'none',
            borderRadius: 10,
            fontWeight: 600,
            cursor: 'pointer',
            fontFamily: 'inherit',
            fontSize: 12.5,
          }}
        >
          {vehicle.status === 'maint' ? 'Clear maintenance' : 'Maintenance mode'}
        </button>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 14, marginBottom: 20 }}>
        {kpiTiles.map((k, i) => (
          <div key={i} style={{ background: '#fff', border: '1px solid #E4E3DE', borderRadius: 18, padding: '16px' }}>
            <div style={{ width: 38, height: 38, borderRadius: 11, background: '#E2F5F8', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <FleetIcon name={k.ic} size={18} color="#00707F" />
            </div>
            <div style={{ fontSize: 26, fontWeight: 700, fontFamily: 'JetBrains Mono, monospace', letterSpacing: -0.5, marginTop: 14 }}>{k.v}</div>
            <div style={{ fontSize: 11.5, color: '#5D646B', marginTop: 3, letterSpacing: 0.3, textTransform: 'uppercase' }}>{k.l}</div>
          </div>
        ))}
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: '55% 45%', gap: 14, alignItems: 'start' }}>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
          <div style={cardStyle}>
            <div style={cardTitleStyle}>Drivers · {vehicleDrivers.length}</div>
            {vehicleDrivers.map((d) => (
              <div key={d.uid} style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '8px 0', borderBottom: '1px solid #EFEDE7' }}>
                <div style={{ width: 36, height: 36, borderRadius: 10, background: '#E2F5F8', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 12, fontWeight: 700, color: '#00707F' }}>
                  {d.name.slice(0, 2).toUpperCase()}
                </div>
                <div>
                  <div style={{ fontSize: 13, fontWeight: 600 }}>{d.name}</div>
                  <div style={{ fontSize: 11, color: '#5D646B' }}>
                    {d.isPrimary ? '● Primary driver' : '○ Secondary driver'}
                  </div>
                </div>
              </div>
            ))}
            {vehicleDrivers.length === 0 && (
              <div style={{ color: '#8C9196', fontSize: 12 }}>No drivers assigned to this vehicle.</div>
            )}
          </div>

          {isCargo ? (
            <div style={cardStyle}>
              <div style={cardTitleStyle}>Cargo carried · {completedTrips.length} trips</div>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1.2fr 1.5fr 0.6fr 0.8fr', gap: 8, padding: '0 0 8px', fontSize: 10, fontWeight: 700, color: '#8C9196', letterSpacing: 0.4, textTransform: 'uppercase', borderBottom: '1px solid #EFEDE7' }}>
                <span>Cargo ref</span>
                <span>Goods</span>
                <span>Route</span>
                <span>Km</span>
                <span>Damage</span>
              </div>
              {[...completedTrips]
                .sort((a, b) => tsMs(b.routeFinishedAt ?? b.routeStartedAt) - tsMs(a.routeFinishedAt ?? a.routeStartedAt))
                .slice(0, 30)
                .map((t) => {
                  const damages = damageByTripId[String(t.id)] ?? [];
                  return (
                    <div key={String(t.id)} style={{ display: 'grid', gridTemplateColumns: '1fr 1.2fr 1.5fr 0.6fr 0.8fr', gap: 8, padding: '10px 0', borderBottom: '1px solid #EFEDE7', alignItems: 'center' }}>
                      <span style={{ fontFamily: 'JetBrains Mono, monospace', fontWeight: 600, fontSize: 12 }}>
                        {String(t.cargoRef || '—')}
                      </span>
                      <span style={{ fontSize: 12, color: '#2A3138' }}>{String(t.goodsDescription || '—')}</span>
                      <span style={{ fontSize: 11, color: '#5D646B' }}>
                        {String(t.origin || '—')} → {String(t.destination || '—')}
                      </span>
                      <span style={{ fontSize: 12, fontFamily: 'JetBrains Mono, monospace' }}>
                        {Number(t.distanceKm ?? 0).toFixed(1)}
                      </span>
                      <span>
                        {damages.length > 0 ? (
                          <span style={{ color: '#B42318', fontSize: 11, fontWeight: 600 }}>
                            ⚠ {damages.length} report{damages.length > 1 ? 's' : ''}
                          </span>
                        ) : (
                          <span style={{ color: '#1E7A4C', fontSize: 11 }}>✓ None</span>
                        )}
                      </span>
                    </div>
                  );
                })}
              {completedTrips.length === 0 && (
                <div style={{ color: '#8C9196', fontSize: 12, padding: '12px 0' }}>No completed cargo trips yet.</div>
              )}
            </div>
          ) : (
            <div style={cardStyle}>
              <div style={cardTitleStyle}>Trip history · {completedTrips.length}</div>
              <div style={{ overflowX: 'auto' }}>
                <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12 }}>
                  <thead>
                    <tr style={{ textAlign: 'left', color: '#8C9196', fontSize: 10.5, textTransform: 'uppercase' }}>
                      <th style={{ padding: '8px 0' }}>Date</th>
                      <th style={{ padding: '8px 0' }}>Route</th>
                      <th style={{ padding: '8px 0' }}>Km</th>
                      <th style={{ padding: '8px 0' }}>Fuel flag</th>
                    </tr>
                  </thead>
                  <tbody>
                    {[...completedTrips]
                      .sort((a, b) => tsMs(b.routeFinishedAt ?? b.routeStartedAt) - tsMs(a.routeFinishedAt ?? a.routeStartedAt))
                      .slice(0, 30)
                      .map((t) => (
                        <tr key={String(t.id)} style={{ borderTop: '1px solid #EFEDE7' }}>
                          <td style={{ padding: '10px 0' }}>{formatDate(t.routeFinishedAt ?? t.routeStartedAt)}</td>
                          <td style={{ padding: '10px 0' }}>
                            {String(t.origin ?? '—')} → {String(t.destination ?? '—')}
                          </td>
                          <td style={{ padding: '10px 0', fontFamily: 'JetBrains Mono, monospace' }}>
                            {Number(t.distanceKm ?? 0).toFixed(1)}
                          </td>
                          <td style={{ padding: '10px 0' }}>{t.needsRefuelSoon ? 'Needs refuel' : '—'}</td>
                        </tr>
                      ))}
                  </tbody>
                </table>
              </div>
              {completedTrips.length === 0 && (
                <div style={{ color: '#8C9196', fontSize: 12, padding: '12px 0' }}>No completed trips yet.</div>
              )}
            </div>
          )}
        </div>

        <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
          <div style={cardStyle}>
            <div style={cardTitleStyle}>Fuel efficiency</div>
            <div style={{ display: 'flex', gap: 20, alignItems: 'center' }}>
              <EfficiencyDonut pct={eff.pct} label={rating} />
              <div style={{ flex: 1 }}>
                <div style={{ fontSize: 12, color: '#5D646B', marginBottom: 4 }}>{eff.confidenceLabel}</div>
                <div style={{ fontSize: 11, color: '#8C9196', lineHeight: 1.5 }}>{eff.tooltipNote}</div>
                {kmPerL != null && (
                  <div style={{ marginTop: 8, fontSize: 13, fontWeight: 700 }}>
                    {kmPerL.toFixed(2)} km/L · {totalLitres.toFixed(0)} L used
                  </div>
                )}
              </div>
            </div>
            <div style={{ marginTop: 16, display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
              <div>
                <label style={inputLabelStyle}>Tank capacity (L)</label>
                <input value={tankInput} onChange={(e) => setTankInput(e.target.value)} style={inputStyle} />
              </div>
              <div>
                <label style={inputLabelStyle}>Benchmark km/L</label>
                <input value={benchInput} onChange={(e) => setBenchInput(e.target.value)} style={inputStyle} />
              </div>
            </div>
            <button
              type="button"
              disabled={saving}
              onClick={() => void handleSave()}
              style={{
                marginTop: 12,
                padding: '8px 14px',
                background: '#00707F',
                color: '#fff',
                border: 'none',
                borderRadius: 10,
                fontWeight: 600,
                cursor: saving ? 'wait' : 'pointer',
                fontFamily: 'inherit',
                fontSize: 12.5,
              }}
            >
              {saving ? 'Saving…' : 'Save benchmark'}
            </button>
            {eff.confidence === 'no_benchmark' && (
              <div style={{ marginTop: 8, fontSize: 11, color: '#9A5B00', fontWeight: 600 }}>
                Set a km/L benchmark above to enable efficiency scoring.
              </div>
            )}
          </div>

          <div style={cardStyle}>
            <div style={cardTitleStyle}>Last trip route</div>
            {lastTripRoutePoints.length >= 2 ? (
              <FleetConsoleMap
                vehicles={[
                  {
                    id: vehicle.id,
                    plate: vehicle.plate,
                    name: vehicle.type,
                    status: vehicle.status,
                    liveLocation: null,
                    routePoints: lastTripRoutePoints,
                    restLocation: restLocation ?? null,
                  },
                ]}
                orgSubtitle=""
                height={220}
                simpleRoute
              />
            ) : (
              <div style={{ height: 140, background: '#F6F5F1', borderRadius: 12, display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#8C9196', fontSize: 12 }}>
                No completed trips with GPS data yet.
              </div>
            )}
            {lastTrip && (
              <div style={{ fontSize: 11, color: '#5D646B', marginTop: 8 }}>
                {formatDate(lastTrip.routeFinishedAt ?? lastTrip.routeStartedAt)} · {Number(lastTrip.distanceKm ?? 0).toFixed(1)} km
              </div>
            )}
          </div>

          <div style={cardStyle}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <div style={cardTitleStyle}>Incidents · {vehicleIssues.length}</div>
              {vehicleIssues.length > 0 && (
                <span style={{ fontSize: 10, fontWeight: 700, color: '#B42318', background: '#FDECEA', padding: '2px 8px', borderRadius: 20 }}>
                  {vehicleIssues.length}
                </span>
              )}
            </div>
            {vehicleIssues.slice(0, 10).map((iss) => (
              <div key={String(iss.id)} style={{ padding: '10px 0', borderBottom: '1px solid #EFEDE7' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 7, flexWrap: 'wrap' }}>
                  <span style={severityBadgeStyle(String(iss.severity ?? 'info'))}>
                    {String(iss.severity ?? 'info').toUpperCase()}
                  </span>
                  <span style={{ fontSize: 12.5, fontWeight: 600, color: '#0F1419' }}>
                    {String(iss.description ?? iss.message ?? 'No description')}
                  </span>
                </div>
                <div style={{ fontSize: 11, color: '#8C9196', marginTop: 4 }}>
                  {formatDate(iss.createdAt ?? iss.reportedAt)}
                </div>
              </div>
            ))}
            {vehicleIssues.length === 0 && (
              <div style={{ fontSize: 12, color: '#5D646B', paddingTop: 8 }}>No incidents reported.</div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

export function DriversListView({
  drivers,
  driverNames,
}: {
  drivers?: Array<{ uid: string; name: string; vehicleId?: string; isPrimary: boolean }>;
  driverNames?: Record<string, string>;
}) {
  const rows =
    drivers ??
    Object.entries(driverNames ?? {}).map(([uid, name]) => ({
      uid,
      name,
      vehicleId: undefined as string | undefined,
      isPrimary: false,
    }));
  return (
    <div style={{ background: '#fff', border: '1px solid #E4E3DE', borderRadius: 18, padding: 16 }}>
      <div style={{ fontSize: 14, fontWeight: 700, marginBottom: 12 }}>Fleet drivers</div>
      {rows.map((d) => (
        <div key={d.uid} style={{ padding: '10px 0', borderBottom: '1px solid #EFEDE7', fontSize: 13 }}>
          <div style={{ fontWeight: 600 }}>{d.name}</div>
          <div style={{ fontSize: 11, color: '#5D646B', marginTop: 2 }}>
            {d.isPrimary ? 'Primary · ' : 'Secondary · '}
            {d.vehicleId ? `Vehicle ${d.vehicleId}` : 'No vehicle assigned'}
          </div>
        </div>
      ))}
      {rows.length === 0 && <div style={{ color: '#5D646B' }}>No drivers registered.</div>}
    </div>
  );
}

export function ClientsListView({ clients }: { clients: { id: string; name: string; email: string }[] }) {
  return (
    <div style={{ background: '#fff', border: '1px solid #E4E3DE', borderRadius: 18, padding: 16 }}>
      <div style={{ fontSize: 14, fontWeight: 700, marginBottom: 12 }}>Cargo clients</div>
      {clients.map((c) => (
        <div key={c.id} style={{ padding: '10px 0', borderBottom: '1px solid #EFEDE7' }}>
          <div style={{ fontSize: 13, fontWeight: 600 }}>{c.name}</div>
          <div style={{ fontSize: 11, color: '#5D646B' }}>{c.email}</div>
        </div>
      ))}
      {clients.length === 0 && <div style={{ color: '#5D646B' }}>No clients yet.</div>}
    </div>
  );
}

export function FleetSettingsView({
  orgName,
  orgCode,
  email,
  onSignOut,
}: {
  orgName: string;
  orgCode: string;
  email?: string | null;
  onSignOut: () => void;
}) {
  const { currentUser } = useAuth();

  // Admin invite (second admin, same org)
  const [adminInviteEmail, setAdminInviteEmail] = useState('');
  const [adminInviteSending, setAdminInviteSending] = useState(false);
  const [adminInviteStatus, setAdminInviteStatus] = useState<'idle' | 'success' | 'error'>('idle');
  const [adminInviteError, setAdminInviteError] = useState('');

  const handleInviteAdmin = async () => {
    const addr = adminInviteEmail.trim().toLowerCase();
    if (!addr || !orgCode || adminInviteSending) return;
    setAdminInviteSending(true);
    setAdminInviteStatus('idle');
    setAdminInviteError('');
    try {
      if (!isPaymentsConfigured()) {
        setAdminInviteStatus('error');
        setAdminInviteError('Email service not configured.');
        setAdminInviteSending(false);
        return;
      }
      const orgQ = query(collection(db, 'organizations'), where('code', '==', orgCode), limit(1));
      const orgSnap = await getDocs(orgQ);
      if (orgSnap.docs[0]) {
        await updateDoc(doc(db, 'organizations', orgSnap.docs[0].id), {
          pendingAdminInvites: arrayUnion(addr),
          updatedAt: new Date(),
        });
      }
      const settings = adminInviteActionSettings(orgCode);
      const branded = await sendAdminInviteEmailApi({
        orgCode,
        email: addr,
        continueUrl: settings.url,
      });
      if (!branded.ok) {
        setAdminInviteStatus('error');
        setAdminInviteError(branded.error || 'Failed to send invite.');
        setAdminInviteSending(false);
        return;
      }
      setAdminInviteStatus('success');
      setAdminInviteEmail('');
    } catch (e: unknown) {
      setAdminInviteStatus('error');
      setAdminInviteError((e as Error)?.message || 'Failed to send invite.');
    }
    setAdminInviteSending(false);
  };

  // Bulk invite parents / drivers
  const [inviteRole, setInviteRole] = useState<'parent' | 'bus_driver'>('parent');
  const [inviteEmails, setInviteEmails] = useState<string[]>([]);
  const [inviteDraft, setInviteDraft] = useState('');
  const [bulkInviting, setBulkInviting] = useState(false);

  const handleBulkInvite = async () => {
    if (!inviteEmails.length || !orgCode || !currentUser || bulkInviting) return;
    setBulkInviting(true);
    let sent = 0, failed = 0;
    for (const em of inviteEmails) {
      const result = await sendFleetInvite(em, orgCode, orgName, inviteRole, currentUser.uid);
      if (result.ok) {
        sent++;
      } else {
        failed++;
        if ((result.error || '').toLowerCase().includes('too-many-requests')) {
          break;
        }
      }
    }
    setBulkInviting(false);
    if (sent > 0) {
      setInviteEmails([]);
      setInviteDraft('');
    }
    // surface result via a simple visual state — lorry console uses its own toast pattern
    if (failed > 0 && sent === 0) setAdminInviteError(`${failed} invite${failed > 1 ? 's' : ''} failed to send`);
  };

  return (
    <div style={{ marginTop: 14, display: 'grid', gap: 14, maxWidth: 520 }}>
      <div style={{ background: '#fff', border: '1px solid #E4E3DE', borderRadius: 18, padding: 20 }}>
        <div style={{ fontSize: 14, fontWeight: 700, marginBottom: 12 }}>Organization</div>
        <div style={{ fontSize: 13, fontWeight: 600 }}>{orgName}</div>
        <div style={{ fontSize: 11, color: '#5D646B', marginTop: 4, fontFamily: 'JetBrains Mono, monospace' }}>{orgCode}</div>
      </div>

      {/* Invite Team — parents & drivers */}
      <div style={{ background: '#fff', border: '1px solid #E4E3DE', borderRadius: 18, padding: 20 }}>
        <div style={{ fontSize: 14, fontWeight: 700, marginBottom: 4 }}>Invite Team</div>
        <div style={{ fontSize: 12, color: '#5D646B', marginBottom: 14 }}>
          Invite parents or drivers. They'll get an email link to set their password and access the PEAR app.
        </div>
        {/* Role toggle */}
        <div style={{ display: 'flex', gap: 6, marginBottom: 12 }}>
          {(['parent', 'bus_driver'] as const).map(r => (
            <button key={r} type="button" onClick={() => setInviteRole(r)}
              style={{ flex: 1, padding: '7px 10px', borderRadius: 8, border: `2px solid ${inviteRole === r ? '#00707F' : '#E4E3DE'}`, background: inviteRole === r ? '#00707F' : '#fff', color: inviteRole === r ? '#fff' : '#5D646B', fontSize: 12, fontWeight: 600, cursor: 'pointer', fontFamily: 'inherit' }}>
              {r === 'parent' ? '👤 Parents' : '🚌 Drivers'}
            </button>
          ))}
        </div>
        {/* Chips */}
        {inviteEmails.length > 0 && (
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginBottom: 8 }}>
            {inviteEmails.map(em => (
              <span key={em} style={{ display: 'flex', alignItems: 'center', gap: 4, background: '#E0F7FA', color: '#00838F', borderRadius: 20, padding: '3px 10px', fontSize: 12, fontWeight: 600 }}>
                {em}
                <button type="button" onClick={() => setInviteEmails(prev => prev.filter(e => e !== em))}
                  style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#00838F', fontSize: 14, lineHeight: 1, padding: 0, marginLeft: 2 }}>×</button>
              </span>
            ))}
          </div>
        )}
        <input
          type="email"
          value={inviteDraft}
          onChange={e => setInviteDraft(e.target.value)}
          onKeyDown={e => {
            if (e.key === 'Enter' || e.key === ',') {
              e.preventDefault();
              const addr = inviteDraft.trim().toLowerCase();
              if (/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(addr) && !inviteEmails.includes(addr)) {
                setInviteEmails(prev => [...prev, addr]);
                setInviteDraft('');
              }
            }
          }}
          placeholder={`${inviteRole === 'parent' ? "Parent's" : "Driver's"} email — press Enter to add`}
          style={{ width: '100%', padding: '10px 12px', borderRadius: 10, border: '1px solid #E4E3DE', fontSize: 13, fontFamily: 'inherit', marginBottom: 6 }}
        />
        <div style={{ fontSize: 11, color: '#8C9196', marginBottom: 10 }}>Press Enter after each email. Add as many as you need.</div>
        <button type="button" onClick={() => void handleBulkInvite()}
          disabled={inviteEmails.length === 0 || bulkInviting}
          style={{ width: '100%', padding: '11px', background: inviteEmails.length === 0 || bulkInviting ? '#8C9196' : '#00707F', color: '#fff', border: 'none', borderRadius: 10, fontSize: 13, fontWeight: 600, cursor: inviteEmails.length === 0 || bulkInviting ? 'not-allowed' : 'pointer', fontFamily: 'inherit' }}>
          {bulkInviting ? 'Sending…' : inviteEmails.length === 0 ? 'Add emails above' : `Send ${inviteEmails.length} ${inviteRole === 'parent' ? 'parent' : 'driver'} invite${inviteEmails.length > 1 ? 's' : ''}`}
        </button>
      </div>

      {/* Invite second admin */}
      <div style={{ background: '#fff', border: '1px solid #E4E3DE', borderRadius: 18, padding: 20 }}>
        <div style={{ fontSize: 14, fontWeight: 700, marginBottom: 4 }}>Admin Team</div>
        <div style={{ fontSize: 12, color: '#5D646B', marginBottom: 12 }}>
          Invite a second fleet admin to access this dashboard.
        </div>
        <div style={{ display: 'flex', gap: 8 }}>
          <input type="email" value={adminInviteEmail}
            onChange={e => { setAdminInviteEmail(e.target.value); setAdminInviteStatus('idle'); }}
            placeholder="admin@organisation.co.ke"
            style={{ flex: 1, padding: '10px 12px', borderRadius: 10, border: '1px solid #E4E3DE', fontSize: 13, fontFamily: 'inherit' }} />
          <button type="button" onClick={handleInviteAdmin} disabled={!adminInviteEmail.trim() || adminInviteSending}
            style={{ padding: '10px 14px', background: adminInviteSending ? '#8C9196' : '#00707F', color: '#fff', border: 'none', borderRadius: 10, fontSize: 13, fontWeight: 600, cursor: adminInviteSending ? 'not-allowed' : 'pointer', whiteSpace: 'nowrap', fontFamily: 'inherit' }}>
            {adminInviteSending ? 'Sending…' : 'Send invite'}
          </button>
        </div>
        {adminInviteStatus === 'success' && (
          <div style={{ marginTop: 10, padding: '8px 12px', background: '#F0FDF4', borderRadius: 8, border: '1px solid #BBF7D0', fontSize: 12, color: '#16A34A', fontWeight: 600 }}>
            Invite sent — they'll get an email link shortly.
          </div>
        )}
        {(adminInviteStatus === 'error' || adminInviteError) && (
          <div style={{ marginTop: 10, padding: '8px 12px', background: '#FEF2F2', borderRadius: 8, border: '1px solid #FECACA', fontSize: 12, color: '#DC2626' }}>
            {adminInviteError || 'Failed to send invite. Please try again.'}
          </div>
        )}
      </div>

      <div style={{ background: '#fff', border: '1px solid #E4E3DE', borderRadius: 18, padding: 20 }}>
        <div style={{ fontSize: 14, fontWeight: 700, marginBottom: 12 }}>Account</div>
        {email && <div style={{ fontSize: 13, color: '#2A3138', marginBottom: 16 }}>{email}</div>}
        <button type="button" onClick={onSignOut}
          style={{ width: '100%', padding: '12px', borderRadius: 10, border: '1px solid #B42318', background: '#FDECEA', color: '#B42318', fontWeight: 600, cursor: 'pointer', fontFamily: 'inherit' }}>
          Sign out
        </button>
      </div>
      <div style={{ background: '#fff', border: '1px solid #E4E3DE', borderRadius: 18, padding: 20 }}>
        <div style={{ fontSize: 14, fontWeight: 700, marginBottom: 8 }}>Notifications</div>
        <div style={{ fontSize: 12, color: '#5D646B' }}>
          Trip alerts and fleet notifications are managed in the Pear mobile app for drivers and owners.
        </div>
      </div>
    </div>
  );
}

export function SignupCodeModal({
  open,
  code,
  expiresAt,
  loading,
  onGenerate,
  onClose,
}: {
  open: boolean;
  code: string | null;
  expiresAt: number | null;
  loading: boolean;
  onGenerate: () => void;
  onClose: () => void;
}) {
  if (!open) return null;
  const minsLeft = expiresAt ? Math.max(0, Math.round((expiresAt - Date.now()) / 60000)) : 0;
  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(15,20,25,0.45)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 100 }}>
      <div style={{ background: '#fff', borderRadius: 18, padding: 24, width: 360, maxWidth: '90vw' }}>
        <div style={{ fontSize: 16, fontWeight: 700 }}>Register a vehicle</div>
        <div style={{ fontSize: 12, color: '#5D646B', marginTop: 6 }}>
          Share this 4-digit code with a driver signing up as Transport in the Pear app. Valid 15 minutes.
        </div>
        {code ? (
          <div style={{ marginTop: 16, fontSize: 32, fontWeight: 700, fontFamily: 'JetBrains Mono, monospace', letterSpacing: 8, textAlign: 'center' }}>{code}</div>
        ) : (
          <div style={{ marginTop: 16, color: '#8C9196', textAlign: 'center' }}>No active code</div>
        )}
        {expiresAt && code && <div style={{ fontSize: 11, color: '#5D646B', textAlign: 'center', marginTop: 8 }}>Expires in ~{minsLeft} min</div>}
        {code && (
          <button
            type="button"
            onClick={() => navigator.clipboard.writeText(code)}
            style={{ width: '100%', marginTop: 12, padding: '8px', background: '#F6F5F1', border: '1px solid #E4E3DE', borderRadius: 10, fontWeight: 600, cursor: 'pointer', fontFamily: 'inherit' }}
          >
            Copy code
          </button>
        )}
        <div style={{ display: 'flex', gap: 8, marginTop: 20 }}>
          <button type="button" disabled={loading} onClick={onGenerate} style={{ flex: 1, padding: '10px', background: '#00707F', color: '#fff', border: 'none', borderRadius: 10, fontWeight: 600, cursor: 'pointer', fontFamily: 'inherit' }}>
            {loading ? 'Generating…' : code ? 'Regenerate' : 'Generate code'}
          </button>
          <button type="button" onClick={onClose} style={{ padding: '10px 16px', background: '#F6F5F1', border: '1px solid #E4E3DE', borderRadius: 10, cursor: 'pointer', fontFamily: 'inherit' }}>Close</button>
        </div>
      </div>
    </div>
  );
}
